import { useEffect } from 'react';

import { env } from '../../config/env';
import type { DocumentHeadOptions } from './useDocumentHead.types';

const DEFAULT_OG_IMAGE_PATH = '/logo512.png';

const updateMetaTag = (
  selector: string,
  attributeName: string,
  attributeValue: string,
  contentValue: string
) => {
  if (!contentValue) return;
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attributeName, attributeValue);
    document.head.appendChild(element);
  }
  element.setAttribute('content', contentValue);
};

export const useDocumentHead = (options: DocumentHeadOptions): void => {
  const { title, description, ogTitle, ogDescription, ogImage } = options;

  useEffect(() => {
    if (title) {
      document.title = title;
    }

    if (description) {
      updateMetaTag('meta[name="description"]', 'name', 'description', description);
    }

    const effectiveOgTitle = ogTitle || title;
    if (effectiveOgTitle) {
      updateMetaTag('meta[property="og:title"]', 'property', 'og:title', effectiveOgTitle);
      updateMetaTag('meta[name="twitter:title"]', 'name', 'twitter:title', effectiveOgTitle);
    }

    const effectiveOgDescription = ogDescription || description;
    if (effectiveOgDescription) {
      updateMetaTag(
        'meta[property="og:description"]',
        'property',
        'og:description',
        effectiveOgDescription
      );
      updateMetaTag(
        'meta[name="twitter:description"]',
        'name',
        'twitter:description',
        effectiveOgDescription
      );
    }

    const effectiveOgImage = ogImage || `${env().PUBLIC_URL}${DEFAULT_OG_IMAGE_PATH}`;
    updateMetaTag('meta[property="og:image"]', 'property', 'og:image', effectiveOgImage);
    updateMetaTag('meta[name="twitter:image"]', 'name', 'twitter:image', effectiveOgImage);
  }, [title, description, ogTitle, ogDescription, ogImage]);
};
